import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark, faSpinner } from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import { CC_PANEL, CC_SUBTLE_LINK } from "@/components/case-checking/caseCheckingChrome";
import {
  CaseDocumentIngestButtons,
  type CaseDocumentIngestProps,
} from "@/components/case-checking/CaseDocumentIngestButtons";

export interface MissingChecklistItem {
  name: string;
  type: string;
  note?: string;
  conditionNote?: string;
}

export function MissingDocumentCards({
  items,
  documentIngest,
  onScrollToItem,
}: {
  items: MissingChecklistItem[];
  documentIngest: CaseDocumentIngestProps;
  onScrollToItem: (documentType: string) => void;
}) {
  if (items.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="missing-document-cards">
      {items.map((item) => {
        const detail = item.note ?? item.conditionNote;
        return (
          <div
            key={item.type}
            className={clsx(CC_PANEL, "space-y-3 border-l-4 border-l-amber-500/80")}
            data-testid={`missing-doc-card-${item.type}`}
          >
            <div className="flex items-start gap-3">
              <FontAwesomeIcon icon={faCircleXmark} className="mt-0.5 h-5 w-5 shrink-0 text-red-500" aria-hidden />
              <div className="min-w-0 flex-1 space-y-3">
                <p className="text-sm font-medium text-rose-700 dark:text-rose-300">{item.name}</p>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  {detail ? (
                    <p className="min-w-0 flex-1 text-sm text-slate-500 dark:text-slate-400">{detail}</p>
                  ) : (
                    <div className="min-w-0 flex-1" />
                  )}
                  <div className="shrink-0">
                    <CaseDocumentIngestButtons
                      documentIngest={documentIngest}
                      compact
                      testIdPrefix={`missing-doc-${item.type}`}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className={CC_SUBTLE_LINK}
                  onClick={() => onScrollToItem(item.type)}
                  data-testid={`missing-doc-card-scroll-${item.type}`}
                >
                  View in requirements
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {documentIngest.ingestError ? (
        <p className="text-sm text-rose-300" role="alert">
          {documentIngest.ingestError}
        </p>
      ) : null}
      {((documentIngest.showIngestProgress ?? documentIngest.isProcessing) && documentIngest.stepLabel) ? (
        <p className="flex items-center gap-2 text-sm text-slate-500">
          <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin text-indigo-500" />
          {documentIngest.stepLabel}
        </p>
      ) : null}
    </div>
  );
}
